import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getNotifications, markNotificationAsSeen, markAllNotificationsAsSeen } from "../../Api/AdminApiCall";

export const fetchNotifications = createAsyncThunk(
    "admin/fetchNotifications",
    async ({ page = 1, pageSize = 20, type = 'notification' } = {}, { rejectWithValue }) => {
        try {
            const res = await getNotifications(page, pageSize, type);
            return res.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || "Failed to load notifications"
            );
        }
    }
);

export const markNotificationAsSeenAction = createAsyncThunk(
    "admin/markNotificationAsSeen",
    async (notificationId, { rejectWithValue }) => {
        try {
            const res = await markNotificationAsSeen(notificationId);
            return { notificationId, data: res.data };
        } catch (error) {
            return rejectWithValue(
                error.response?.data || "Failed to mark notification as seen"
            );
        }
    }
);

export const markAllNotificationsAsSeenAction = createAsyncThunk(
    "admin/markAllNotificationsAsSeen",
    async (_, { rejectWithValue }) => {
        try {
            const res = await markAllNotificationsAsSeen();
            return res.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data || "Failed to mark all notifications as seen"
            );
        }
    }
);

const notificationSlice = createSlice({
    name: "notifications",
    initialState: {
        items: [],
        total: 0,
        page: 1,
        pageSize: 20,
        unreadCount: 0,
        loading: false,
        error: null,
    },
    reducers: {
        clearNotifications: (state) => {
            state.items = [];
            state.total = 0;
            state.page = 1;
            state.unreadCount = 0;
            state.error = null;
        },
        updateNotificationSeenStatus: (state, action) => {
            const notification = state.items.find(item => item.id === action.payload);
            if (notification) {
                notification.is_seen = true;
                notification.seen_at = new Date().toISOString();
                if (state.unreadCount > 0) {
                    state.unreadCount -= 1;
                }
            }
        },
        markAllAsSeen: (state) => {
            state.items.forEach(notification => {
                notification.is_seen = true;
                notification.seen_at = new Date().toISOString();
            });
            state.unreadCount = 0;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchNotifications.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload?.data) {
                    state.items = action.payload.data.items || [];
                    state.total = action.payload.data.total || 0;
                    state.page = action.payload.data.page || 1;
                    state.pageSize = action.payload.data.page_size || 20;
                    state.unreadCount = action.payload.data.unread_count || 0;
                }
            })
            .addCase(fetchNotifications.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(markNotificationAsSeenAction.pending, (state) => {
                // Optional: You can add a loading state for individual notifications
            })
            .addCase(markNotificationAsSeenAction.fulfilled, (state, action) => {
                const notification = state.items.find(item => item.id === action.payload.notificationId);
                if (notification) {
                    notification.is_seen = true;
                    notification.seen_at = new Date().toISOString();
                    if (state.unreadCount > 0) {
                        state.unreadCount -= 1;
                    }
                }
            })
            .addCase(markNotificationAsSeenAction.rejected, (state, action) => {
                // Handle error if needed
            })
            .addCase(markAllNotificationsAsSeenAction.pending, (state) => {
                // Optional: You can add a loading state
            })
            .addCase(markAllNotificationsAsSeenAction.fulfilled, (state) => {
                // Mark all notifications as seen
                state.items.forEach(notification => {
                    notification.is_seen = true;
                    notification.seen_at = new Date().toISOString();
                });
                state.unreadCount = 0;
            })
            .addCase(markAllNotificationsAsSeenAction.rejected, (state, action) => {
                // Handle error if needed
            });
    },
});

export const { clearNotifications, updateNotificationSeenStatus, markAllAsSeen } = notificationSlice.actions;

export default notificationSlice.reducer;
